import React, { useEffect, useState } from 'react';
import useAolSaveHandler from '../../../../app/useAolSaveHandler';
import { NoonReportSectionEnum } from '../../../../types/BusinessModel/AolEnums';
import { AolDataItem } from '../../../../types/ViewModel/AolItem';
import { Vessel } from '../../../../types/ViewModel/Vessel/Vessel';
import {
    aolConstants,
    getConsumptionCost,
    getConsumptionCostByFuelType,
    hasInvalidFields,
    hasMissingValues,
} from '../../../../utils/aolFieldsHelper';
import { ErrorText, SoftErrorText } from '../../../shared/error-text/ErrorText.style';
import { ListHeaderStyle, SubSectionHeader } from '../../aol-container/AolContainer.style';
import OdfjellInputComponent, { InputChangeData, TimeInputChangeData } from '../../../common/odfjell-input-component/OdfjellInputComponent';
import { EngineDataVM, EnginesDataVM } from '../EnginesSection';
import { EngineListHeader, EngineListRow, EngineListValuesWrapper, EngineListWrapper, EngineWrapper } from '../EnginesSection.style';
import { roundTo2Decimals } from '../../../../utils/numberHelper';
import useAolItemsHelper from '../../../../utils/aol/useAolItemsHelper';
import ConsumptionContainer from '../../consumption/ConsumptionContainer';
import { EngineDataTypeEnum } from 'src/types/ViewModel/AolEnums';
import { Consumption } from 'src/types/ViewModel/Aol/Consumption';
import { isNullOrUndefined } from 'src/utils/objectUtils';
import { FuelType } from 'src/types/ViewModel/Bunkering/FuelType';

type Props = {
    enginesDataVM: EnginesDataVM;
    onChange: () => void;
    vessel: Vessel | null;
    aolVersion: number;
    aolDataItem: AolDataItem;
    hasEmptyValues;
    isReadOnly: boolean;
    addConsumptionRow: (consumption: Consumption[]) => void;
};

const AuxiliaryEnginesSectionComponent: React.FC<Props> = ({
    enginesDataVM,
    onChange,
    vessel,
    aolVersion,
    aolDataItem,
    hasEmptyValues,
    isReadOnly,
    addConsumptionRow,
}) => {
    const [canUseHfo, setCanUseHfo] = useState(false);
    const [showPowerLoggedOutput, setShowPowerLoggedOutput] = useState(false);
    const [showPowerOutput, setShowPowerOutput] = useState(false);
    const [showLoadValue, setShowLoadValue] = useState(false);
    const [showShaftGenerator, setShowShaftGenerator] = useState(false);
    const [hasEmptyValuesLocal, setHasEmptyValuesLocal] = useState(false);
    const [hasSoftAuxEngineWarning, setHasSoftAuxEngineWarning] = useState(false);
    const { saveAolEvent } = useAolSaveHandler();
    const [isMetricTonnInconsistant, setIsMetricTonnInconsistant] = useState(false);
    const [isRunningHoursInconsistant, setIsRunningHoursInconsistant] = useState(false);
    const { getVoyageFuelPrice } = useAolItemsHelper();

    useEffect(() => {
        if (!vessel?.vesselConfigurationV3) return;
        initDataVessel(vessel);
    }, [vessel]);

    useEffect(() => {
        if (
            saveAolEvent &&
            saveAolEvent.type === 'save_section' &&
            saveAolEvent.section === NoonReportSectionEnum.Engines &&
            aolDataItem &&
            saveAolEvent.aolDataItem.id === aolDataItem.id
        ) {
            checkOnSave();
        }
    }, [saveAolEvent]);

    const initDataVessel = (vessel: Vessel) => {
        if (!vessel?.vesselConfigurationV3) return;

        const hasPowerLogging = vessel?.vesselConfigurationV3.isEquippedWithPowerLoggingForAuxiliaryEngine;

        setCanUseHfo(
            vessel.vesselConfigurationV3.engineConfiguration.auxiliaryEnginesList
                ? vessel.vesselConfigurationV3.engineConfiguration.auxiliaryEnginesList.some((a) => a.canUseHfo)
                : true
        );
        setShowPowerLoggedOutput(hasPowerLogging && aolVersion >= 2);
        setShowPowerOutput(!hasPowerLogging && aolVersion >= 2);
        setShowShaftGenerator(vessel.vesselConfigurationV3.isEquippedWithShaftGenerator && aolDataItem?.reportType !== 2);
        setShowLoadValue(
            vessel.vesselConfigurationV3.engineConfiguration.auxiliaryEnginesList
                ? vessel.vesselConfigurationV3.engineConfiguration.auxiliaryEnginesList.some((a) => a.maximumLoad !== undefined) && aolVersion >= 2
                : false
        );
    };

    const onInputChange = (changeObject: InputChangeData) => {
        const name = changeObject.event.target.name;
        const value = changeObject.event.target.value;
        const inputId = changeObject.inputId ?? '';
        changeObject.dataObject[name] = value ? +value : undefined;
        const voyageFuelPrice = getVoyageFuelPrice(changeObject.extraArgs);

        if (name === 'auxiliaryEnginesConsumptionHfo') {
            changeObject.dataObject.auxiliaryEnginesConsumptionHfoCost = getConsumptionCost(FuelType.HFO, voyageFuelPrice, +value);
        } else if (name === 'auxiliaryEnginesConsumptionMgo') {
            changeObject.dataObject.auxiliaryEnginesConsumptionMgoCost = getConsumptionCost(FuelType.LGO, voyageFuelPrice, +value);
        } else if (name === 'consumptionMt') {
            if (changeObject.dataObject.consumptionMt === undefined) changeObject.dataObject.consumptionMt = 0;
            changeObject.dataObject.cost = getConsumptionCostByFuelType(changeObject.dataObject as Consumption, voyageFuelPrice);
        }
        resetHasEmptyValues();
        hasInvalidFields(enginesDataVM, `${name}${inputId}`, changeObject.isValid);
        onChange();
        checkRunningHours();
    };

    const getMaxAePowerOutput = (aeIndex: number) => {
        const defaultValue = aolConstants.DEFAULT_MAX_POWER_OUTPUT;

        const auxiliaryEnginesList = vessel?.vesselConfigurationV3?.engineConfiguration.auxiliaryEnginesList;
        if (auxiliaryEnginesList?.length && auxiliaryEnginesList.length > aeIndex) {
            const maxPowerOutput = auxiliaryEnginesList[aeIndex].maximumLoad;
            return maxPowerOutput !== undefined ? maxPowerOutput : defaultValue;
        }

        return defaultValue;
    };

    const getMaxShaftPowerOutput = () => {
        const defaultValue = aolConstants.DEFAULT_MAX_POWER_OUTPUT;

        const shaftMaximumLoad = vessel?.vesselConfigurationV3?.engineConfiguration.shaftMaximumLoad;

        if (showPowerLoggedOutput) {
            return shaftMaximumLoad !== undefined ? shaftMaximumLoad : defaultValue;
        }

        return shaftMaximumLoad ?? defaultValue;
    };

    const getMaxAePowerLoggedOutput = (aux: EngineDataVM, aeIndex: number) => {
        const defaultValue = aolConstants.DEFAULT_MAX_POWER_LOGGED_OUTPUT;
        if (aux?.runningHours === undefined) return defaultValue;

        const auxiliaryEnginesList = vessel?.vesselConfigurationV3?.engineConfiguration.auxiliaryEnginesList;
        if (auxiliaryEnginesList?.length && auxiliaryEnginesList.length > aeIndex) {
            const maxPowerOutput = auxiliaryEnginesList[aeIndex].maximumLoad;
            return maxPowerOutput !== undefined ? maxPowerOutput * aux.runningHours : defaultValue;
        }
        return defaultValue;
    };

    const getMaxShaftPowerLoggedOutput = (runningHours?: number) => {
        const defaultValue = aolConstants.DEFAULT_MAX_POWER_LOGGED_OUTPUT;
        if (runningHours === undefined) return defaultValue;

        const shaftMaximumLoad = vessel?.vesselConfigurationV3?.engineConfiguration.shaftMaximumLoad;
        if (showPowerLoggedOutput) {
            return shaftMaximumLoad !== undefined ? shaftMaximumLoad * runningHours : defaultValue;
        }

        return shaftMaximumLoad ?? defaultValue;
    };

    const getLoadInPercent = (engineData: EngineDataVM, aeIndex?: number | undefined) => {
        const isAuxEngine = aeIndex !== undefined;
        const maxPowerOutput = isAuxEngine ? getMaxAePowerOutput(aeIndex) : getMaxShaftPowerOutput();

        const powerOutput = showPowerLoggedOutput ? getPowerOutputFromPowerLoggedOutput(engineData) : engineData?.powerOutput;

        return maxPowerOutput && powerOutput ? (powerOutput / maxPowerOutput) * 100 : 0;
    };

    const getPowerOutputFromPowerLoggedOutput = (engineData?: EngineDataVM) => {
        if (!engineData || !engineData.runningHours || !engineData.powerLoggedOutput) return 0;

        return engineData.runningHours > 0 ? engineData.powerLoggedOutput / engineData.runningHours : 0;
    };

    const onTimeInputChange = (changeObject: TimeInputChangeData) => {
        changeObject.dataObject[changeObject.name] = changeObject.hours;
        resetHasEmptyValues();
        hasInvalidFields(enginesDataVM, changeObject.name, changeObject.isValid);
        validateFieldsOnRunningHoursChange();
        onChange();
        checkRunningHours();
    };

    const validateFieldsOnRunningHoursChange = () => {
        if (showPowerLoggedOutput) {
            enginesDataVM.auxiliaryEnginesData.forEach((aux, i) => {
                const maxAePowerLoggedOutput = getMaxAePowerLoggedOutput(aux, i);
                const powerLoggedOutput = aux.powerLoggedOutput;
                const isValid = !isNullOrUndefined(powerLoggedOutput) && powerLoggedOutput <= maxAePowerLoggedOutput;
                hasInvalidFields(enginesDataVM, `powerLoggedOutput${i}`, isValid);
            });

            if (showShaftGenerator) {
                const maxShaftPowerLoggedOutput = getMaxShaftPowerLoggedOutput(enginesDataVM.shaftGeneratorData?.runningHours);
                const powerLoggedOutput = enginesDataVM.shaftGeneratorData?.powerLoggedOutput;
                const isValid = !isNullOrUndefined(powerLoggedOutput) && powerLoggedOutput <= maxShaftPowerLoggedOutput;
                hasInvalidFields(enginesDataVM, `powerLoggedOutputshaftGeneratorData`, isValid);
            }
        }
    };

    const resetHasEmptyValues = () => {
        if (hasEmptyValuesLocal) setHasEmptyValuesLocal(false);
        if (hasSoftAuxEngineWarning) setHasSoftAuxEngineWarning(false);
    };

    const checkRunningHours = () => {
        let totRunningHours = 0;
        enginesDataVM.auxiliaryEnginesData?.forEach((auxEngine) => {
            if (auxEngine?.runningHours) {
                totRunningHours += auxEngine.runningHours;
            }
        });

        let runningHoursInconsistency = false;

        if (
            aolDataItem.aolVersion >= 5 &&
            totRunningHours === 0 &&
            enginesDataVM.consumption?.some((c) => c.engineDataType === EngineDataTypeEnum.AuxiliaryEngine && c.consumptionMt > 0)
        ) {
            runningHoursInconsistency = true;
        }

        if (
            aolDataItem.aolVersion < 5 &&
            totRunningHours === 0 &&
            ((enginesDataVM.auxiliaryEnginesConsumptionHfo && enginesDataVM.auxiliaryEnginesConsumptionHfo > 0) ||
                (enginesDataVM.auxiliaryEnginesConsumptionMgo && enginesDataVM.auxiliaryEnginesConsumptionMgo > 0))
        ) {
            runningHoursInconsistency = true;
        }

        setIsRunningHoursInconsistant(runningHoursInconsistency);
        hasEmptyValues.current = runningHoursInconsistency;

        if (
            (aolDataItem.aolVersion >= 5 &&
                totRunningHours > 0 &&
                !enginesDataVM.consumption?.some((c) => c.engineDataType === EngineDataTypeEnum.AuxiliaryEngine && c.consumptionMt > 0)) ||
            (aolDataItem.aolVersion < 5 &&
                totRunningHours > 0 &&
                enginesDataVM.auxiliaryEnginesConsumptionHfo === 0 &&
                enginesDataVM.auxiliaryEnginesConsumptionMgo === 0)
        ) {
            setIsMetricTonnInconsistant(true);
        } else {
            setIsMetricTonnInconsistant(false);
        }
    };

    const checkAuxEnginesSoftWarning = () => {
        let hasData = enginesDataVM.auxiliaryEnginesData?.some(
            (ed) =>
                (ed.runningHours && ed.runningHours > 0) ||
                (ed.powerLoggedOutput && ed.powerLoggedOutput > 0) ||
                (ed.powerOutput && ed.powerOutput > 0)
        );

        if (!hasData && enginesDataVM.shaftGeneratorData?.runningHours) {
            hasData = enginesDataVM.shaftGeneratorData.runningHours > 0;
        }
        if (!hasData && enginesDataVM.shaftGeneratorData?.powerLoggedOutput) {
            hasData = enginesDataVM.shaftGeneratorData.powerLoggedOutput > 0;
        }
        if (!hasData && enginesDataVM.shaftGeneratorData?.powerOutput) {
            hasData = enginesDataVM.shaftGeneratorData.powerOutput > 0;
        }

        if (!hasData) {
            setHasSoftAuxEngineWarning(true);
        } else if (hasSoftAuxEngineWarning) {
            setHasSoftAuxEngineWarning(false);
        }
    };

    const checkOnSave = () => {
        const ignoreList: string[] = ['consumptionHfoCost', 'consumptionMgoCost'];
        const auxData = {
            auxiliaryEnginesConsumptionMgo: enginesDataVM.auxiliaryEnginesConsumptionMgo,
            auxiliaryEnginesConsumptionHfo: enginesDataVM.auxiliaryEnginesConsumptionHfo,
            auxiliaryEnginesData: enginesDataVM.auxiliaryEnginesData,
            shaftGeneratorData: enginesDataVM.shaftGeneratorData,
        };

        if (!canUseHfo) {
            ignoreList.push('auxiliaryEnginesConsumptionHfo');
        }

        let hasMissing = hasMissingValues(auxData, ignoreList);
        if (!hasMissing) {
            ignoreList.push('pitch');
            ignoreList.push('consumptionHfo');
            ignoreList.push('consumptionMgo');
            if (!showPowerLoggedOutput) {
                ignoreList.push('powerLoggedOutput');
            }
            if (!showPowerOutput) {
                ignoreList.push('powerOutput');
            }
            hasMissing = !auxData.auxiliaryEnginesData || auxData.auxiliaryEnginesData.some((a) => hasMissingValues(a, ignoreList));
            if (!hasMissing && showShaftGenerator) {
                hasMissing = hasMissingValues(auxData.shaftGeneratorData, ignoreList);
            }
        }

        if (hasMissing) {
            setHasEmptyValuesLocal(true);
            hasEmptyValues.current = true;
        } else {
            if (hasEmptyValuesLocal) setHasEmptyValuesLocal(false);
            hasEmptyValues.current = false;
        }
        checkAuxEnginesSoftWarning();
        checkRunningHours();
    };

    return (
        <>
            <EngineWrapper>
                <SubSectionHeader>Auxiliary engines</SubSectionHeader>
                {isRunningHoursInconsistant && <ErrorText>Running hours cannot be 0</ErrorText>}
                {isMetricTonnInconsistant && <ErrorText>Bunkers consumption cannot be 0</ErrorText>}
                <EngineListWrapper>
                    {aolDataItem.aolVersion >= 5 ? (
                        <ConsumptionContainer
                            aolItem={aolDataItem}
                            engineType={EngineDataTypeEnum.AuxiliaryEngine}
                            consumption={enginesDataVM?.consumption}
                            isReadOnly={isReadOnly}
                            onChange={onInputChange}
                            addConsumptionRow={addConsumptionRow}
                        />
                    ) : (
                        <>
                            {canUseHfo && (
                                <OdfjellInputComponent
                                    type={'number'}
                                    isReadOnly={isReadOnly}
                                    extraArgs={aolDataItem}
                                    description={'HFO'}
                                    unit={'MT'}
                                    name={'auxiliaryEnginesConsumptionHfo'}
                                    dataObject={enginesDataVM}
                                    min={0}
                                    max={100}
                                    cost={enginesDataVM.auxiliaryEnginesConsumptionHfoCost}
                                    value={enginesDataVM.auxiliaryEnginesConsumptionHfo}
                                    onChange={onInputChange}
                                />
                            )}
                            <OdfjellInputComponent
                                type={'number'}
                                isReadOnly={isReadOnly}
                                extraArgs={aolDataItem}
                                description={'MGO'}
                                unit={'MT'}
                                name={'auxiliaryEnginesConsumptionMgo'}
                                dataObject={enginesDataVM}
                                min={0}
                                max={100}
                                cost={enginesDataVM.auxiliaryEnginesConsumptionMgoCost}
                                value={enginesDataVM.auxiliaryEnginesConsumptionMgo}
                                onChange={onInputChange}
                            />
                        </>
                    )}
                </EngineListWrapper>
                <EngineListWrapper>
                    <EngineListRow resetCols={true}>
                        <ListHeaderStyle>
                            <EngineListValuesWrapper gridCols={4}>
                                <span>Aux. engine</span>
                                <span>Running hours</span>
                                {showPowerLoggedOutput && <span title="Power logged output">Power logged output</span>}
                                {showPowerOutput && <span>Power output</span>}
                                {showLoadValue && <EngineListHeader>Load</EngineListHeader>}
                            </EngineListValuesWrapper>
                        </ListHeaderStyle>
                    </EngineListRow>

                    {enginesDataVM.auxiliaryEnginesData &&
                        enginesDataVM.auxiliaryEnginesData.map((aux, i) => (
                            <EngineListRow key={`${aux.type}_${i}`} resetCols={true}>
                                <EngineListValuesWrapper gridCols={4}>
                                    <div>{aux.engineNumber}</div>
                                    <OdfjellInputComponent
                                        type={'time'}
                                        isReadOnly={isReadOnly}
                                        extraArgs={aolDataItem}
                                        value={aux.runningHours}
                                        onTimeInputChange={onTimeInputChange}
                                        name={'runningHours'}
                                        inputId={i}
                                        dataObject={aux}
                                        max={roundTo2Decimals(aolDataItem.reportHours)}
                                    />
                                    {showPowerLoggedOutput && (
                                        <OdfjellInputComponent
                                            unit={'kWh'}
                                            type={'number'}
                                            isReadOnly={isReadOnly}
                                            extraArgs={aolDataItem}
                                            name={'powerLoggedOutput'}
                                            inputId={i}
                                            dataObject={aux}
                                            min={0}
                                            max={getMaxAePowerLoggedOutput(aux, i)}
                                            value={aux.powerLoggedOutput}
                                            onChange={onInputChange}
                                        />
                                    )}
                                    {showPowerOutput && (
                                        <OdfjellInputComponent
                                            unit={'kW'}
                                            type={'number'}
                                            isReadOnly={isReadOnly}
                                            extraArgs={aolDataItem}
                                            name={'powerOutput'}
                                            inputId={i}
                                            dataObject={aux}
                                            min={0}
                                            max={getMaxAePowerOutput(i)}
                                            value={aux.powerOutput}
                                            onChange={onInputChange}
                                        />
                                    )}
                                    {showLoadValue && (
                                        <OdfjellInputComponent
                                            unit={'%'}
                                            type={'number'}
                                            isReadOnly={true}
                                            value={getLoadInPercent(aux, i).toFixed(0)}
                                        />
                                    )}
                                </EngineListValuesWrapper>
                            </EngineListRow>
                        ))}
                    {showShaftGenerator && (
                        <EngineListRow resetCols={true}>
                            <EngineListValuesWrapper gridCols={4}>
                                <div>Shaft gen.</div>
                                <OdfjellInputComponent
                                    type={'time'}
                                    isReadOnly={isReadOnly}
                                    extraArgs={aolDataItem}
                                    value={enginesDataVM.shaftGeneratorData?.runningHours}
                                    onTimeInputChange={onTimeInputChange}
                                    name={'runningHours'}
                                    inputId={'shaftGeneratorData'}
                                    dataObject={enginesDataVM.shaftGeneratorData}
                                    max={roundTo2Decimals(aolDataItem.reportHours)}
                                ></OdfjellInputComponent>
                                {showPowerLoggedOutput && (
                                    <OdfjellInputComponent
                                        unit={'kWh'}
                                        type={'number'}
                                        isReadOnly={isReadOnly}
                                        extraArgs={aolDataItem}
                                        name={'powerLoggedOutput'}
                                        inputId={'shaftGeneratorData'}
                                        dataObject={enginesDataVM.shaftGeneratorData}
                                        min={0}
                                        max={getMaxShaftPowerLoggedOutput(enginesDataVM.shaftGeneratorData?.runningHours)}
                                        value={enginesDataVM.shaftGeneratorData?.powerLoggedOutput}
                                        onChange={onInputChange}
                                    />
                                )}
                                {showPowerOutput && (
                                    <OdfjellInputComponent
                                        unit={'kW'}
                                        type={'number'}
                                        isReadOnly={isReadOnly}
                                        extraArgs={aolDataItem}
                                        name={'powerOutput'}
                                        inputId={'shaftGeneratorData'}
                                        dataObject={enginesDataVM.shaftGeneratorData}
                                        min={0}
                                        max={getMaxShaftPowerOutput()}
                                        value={enginesDataVM.shaftGeneratorData?.powerOutput}
                                        onChange={onInputChange}
                                    />
                                )}
                                {showLoadValue && (
                                    <OdfjellInputComponent
                                        unit={'%'}
                                        type={'number'}
                                        isReadOnly={true}
                                        value={getLoadInPercent(enginesDataVM.shaftGeneratorData).toFixed(0)}
                                    />
                                )}
                            </EngineListValuesWrapper>
                        </EngineListRow>
                    )}
                </EngineListWrapper>
            </EngineWrapper>
            {hasEmptyValuesLocal && <ErrorText>You must enter all auxiliary engine data before saving.</ErrorText>}
            {hasSoftAuxEngineWarning && <SoftErrorText>No auxiliary engine data entered.</SoftErrorText>}
        </>
    );
};

export default AuxiliaryEnginesSectionComponent;
